import type { ThunkApi } from "@src/store/rootState";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { actions } from "@src/store/slices/experienceBuilder/common/actions";
import { getVariationDraft } from "@src/store/slices/experienceBuilder/common/selectors/getVariationDraft";
import { hasVariationDraftChanges } from "@src/store/slices/experienceBuilder/common/selectors/hasVariationDraftChanges";
import { applyDraft } from "@src/store/slices/experienceBuilder/common/thunks/applyDraft";
import {
  trackDiscardModalClosed,
  trackDiscardModalViewed,
  trackModEditCancelled,
} from "@src/store/slices/experienceBuilder/common/thunks/mixPanel";
import { confirm } from "@src/store/slices/experienceBuilder/common/utils/confirm";

export interface CancelDraftOptions {
  askConfirmation?: boolean;
  trackCancel?: boolean;
}

export const cancelDraft = createAsyncThunk<boolean, CancelDraftOptions | void>(
  "experienceBuilder/cancelDraft",
  async (opts, api) => {
    const options = opts ?? {};
    const { trackCancel = false } = options;

    const result = await executeCancel(options, api);
    if (result && trackCancel) {
      const hasUnsavedChanges = hasVariationDraftChanges(api.getState());
      const mod = getVariationDraft(api.getState());
      if (mod) {
        trackModEditCancelled({ mod, hasUnsavedChanges });
      }
    }
    return result;
  }
);

async function executeCancel(options: CancelDraftOptions, thunkApi: ThunkApi) {
  const { getState, dispatch } = thunkApi;
  const { askConfirmation = true } = options;

  const mod = getVariationDraft(getState());
  if (!mod) {
    return true;
  }

  const hasChanges = hasVariationDraftChanges(getState());
  if (!askConfirmation || !hasChanges) {
    dispatch(actions.clearDraft());
    return true;
  }

  void dispatch(trackDiscardModalViewed({ source: "modification_edit" }));

  const result = await confirm(dispatch, {
    title: "Apply changes to this modification?",
    message: "You have unapplied changes to this modification.",
    confirmText: "Apply changes",
    cancelText: "Discard",
    showCloseIcon: true,
  });

  if (result === "confirm") {
    void dispatch(applyDraft());
    void dispatch(trackDiscardModalClosed({ choice: "apply_changes" }));
    return false;
  }

  if (result === "cancel") {
    dispatch(actions.clearDraft());
    void dispatch(trackDiscardModalClosed({ choice: "discard_and_exit" }));
    return true;
  }

  void dispatch(trackDiscardModalClosed({ choice: "keep_editing" }));
  return false;
}
